import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { APP_VERSION } from '../version';
import { activateUpdate, resetServiceWorker, UPDATE_RESULT } from '../utils/swUpdate';
import './InstallBanner.css';

const DISMISS_KEY  = 'update_banner_dismissed_v';
// Der Nutzer hat zugestimmt — ab jetzt läuft das Update im Hintergrund, auch über
// Reloads hinweg, bis die neue Version wirklich geladen ist.
const UPDATING_KEY = 'update_banner_updating_v';
// Der harte Reset (Worker + Caches weg) wurde für diese Version schon einmal ausgeführt.
// Loop-Schutz: danach wird NICHT noch einmal hart zurückgesetzt, nur weiter gepollt.
const FORCED_KEY   = 'update_banner_forced_v';

// So lange wird höflich versucht (wartenden Worker aktivieren), bevor hart zurückgesetzt wird.
const POLITE_WINDOW_MS = 90 * 1000;
const POLL_DELAY_MS    = 3000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function UpdateBanner() {
  const [show,          setShow]          = useState(false);
  const [latestVersion, setLatestVersion] = useState('');
  // updating = der Nutzer hat zugestimmt; ab hier nur noch der schmale Fortschritts-Streifen,
  // keine Buttons mehr, bis die neue Version geladen ist.
  const [updating,      setUpdating]      = useState(false);

  const abortRef   = useRef(false);   // „Später" im Zustimmungs-Zustand → Auto-Retry stoppen
  const runningRef = useRef(false);   // verhindert doppelten Start des Hintergrund-Laufs
  const bannerRef  = useRef(null);

  // Unmountet die Komponente (in der Praxis nur beim App-Teardown), wird der laufende
  // Hintergrund-Loop gestoppt — ein Remount nimmt ihn über UPDATING_KEY wieder auf.
  useEffect(() => () => { abortRef.current = true; }, []);

  // Platz freihalten, damit der Streifen keine Bedienelemente (Administration/Abmelden …)
  // verdeckt: Body-Klasse + gemessene Banner-Höhe → das UI oben wird gekürzt, nicht überdeckt.
  useLayoutEffect(() => {
    if (!show) {
      document.body.classList.remove('ts-banner-open');
      document.body.style.removeProperty('--ts-banner-h');
      return;
    }
    document.body.classList.add('ts-banner-open');
    const h = bannerRef.current ? bannerRef.current.offsetHeight : 0;
    document.body.style.setProperty('--ts-banner-h', `${h}px`);
    return () => {
      document.body.classList.remove('ts-banner-open');
      document.body.style.removeProperty('--ts-banner-h');
    };
  }, [show, updating, latestVersion]);

  // Läuft im Hintergrund, bis die neue Version geladen ist — eskaliert bei Bedarf hart.
  const runBackgroundUpdate = useCallback(async (version) => {
    if (runningRef.current) return;
    runningRef.current = true;
    abortRef.current = false;

    const alreadyForced = sessionStorage.getItem(FORCED_KEY) === version;

    if (!alreadyForced) {
      // 1) Höfliche Phase: wartenden Worker aktivieren (behält alle Daten), zeitlich begrenzt.
      const deadline = Date.now() + POLITE_WINDOW_MS;
      while (Date.now() < deadline && !abortRef.current) {
        let result;
        try { result = await activateUpdate(); } catch { result = UPDATE_RESULT.STILL_INSTALLING; }
        if (abortRef.current) { runningRef.current = false; return; }
        if (result === UPDATE_RESULT.ACTIVATED) { window.location.reload(); return; }
        // Kein per-Worker erkennbares Update → direkt zur harten Phase.
        if (result === UPDATE_RESULT.NO_UPDATE || result === UPDATE_RESULT.UNSUPPORTED) break;
        await sleep(POLL_DELAY_MS); // STILL_INSTALLING → weiter warten
      }
      if (abortRef.current) { runningRef.current = false; return; }

      // 2) Harte Phase (genau EINMAL pro Version, Loop-Schutz): Worker + Caches wegräumen und
      //    frisch aus dem Netz laden. Löscht KEINE Nutzerdaten (localStorage bleibt) — der
      //    Nutzer wird nicht abgemeldet, nur der alte Cache wird erzwungen aufgelöst.
      sessionStorage.setItem(FORCED_KEY, version);
      try { await resetServiceWorker(); } catch { /* egal — der Reload folgt */ }
      if (abortRef.current) { runningRef.current = false; return; }
      const url = new URL(window.location.href);
      url.searchParams.set('_u', Date.now().toString()); // Browser-Cache für index.html umgehen
      window.location.replace(url.toString());
      return;
    }

    // 3) Schon einmal hart zurückgesetzt, immer noch alt (z. B. Deploy noch nicht propagiert):
    //    Banner bleibt stehen, wir pollen unbegrenzt weiter — Reload nur bei echter Übernahme.
    //    Kein erneuter harter Reset (das würde eine Reload-Schleife auslösen).
    while (!abortRef.current) {
      let result;
      try { result = await activateUpdate(); } catch { result = UPDATE_RESULT.STILL_INSTALLING; }
      if (abortRef.current) { runningRef.current = false; return; }
      if (result === UPDATE_RESULT.ACTIVATED) { window.location.reload(); return; }
      await sleep(POLL_DELAY_MS);
    }
    runningRef.current = false;
  }, []);

  const checkForUpdate = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const { version } = await res.json();

      if (!version || version === APP_VERSION) {
        // Auf der neuen Version angekommen — alles aufräumen, Banner weg.
        sessionStorage.removeItem(UPDATING_KEY);
        sessionStorage.removeItem(FORCED_KEY);
        setUpdating(false);
        setShow(false);
        return;
      }

      // Läuft bereits ein zugestimmtes Update (über den Reload hinweg)? Direkt weiterlaufen —
      // ohne Buttons, ohne erneute Nachfrage.
      if (sessionStorage.getItem(UPDATING_KEY) === version) {
        setLatestVersion(version);
        setUpdating(true);
        setShow(true);
        runBackgroundUpdate(version);
        return;
      }

      // Für diese Version bewusst „Später" gewählt.
      if (sessionStorage.getItem(DISMISS_KEY) === version) return;

      setLatestVersion(version);
      setShow(true);
    } catch {
      // Kein Update-Check möglich – still ignorieren
    }
  }, [runBackgroundUpdate]);

  useEffect(() => {
    checkForUpdate();

    // Alle 10 Minuten erneut prüfen (relevant für lang geöffnete PWA-Sessions)
    const interval = setInterval(checkForUpdate, 10 * 60 * 1000);

    // Meldet der Service Worker selbst eine neue Version, sofort nachsehen
    const onSwUpdate = () => checkForUpdate();
    window.addEventListener('swUpdateReady', onSwUpdate);

    return () => {
      clearInterval(interval);
      window.removeEventListener('swUpdateReady', onSwUpdate);
    };
  }, [checkForUpdate]);

  function handleConsent() {
    // Ab hier keine Buttons mehr — läuft im Hintergrund bis zur neuen Version.
    sessionStorage.setItem(UPDATING_KEY, latestVersion);
    setUpdating(true);
    runBackgroundUpdate(latestVersion);
  }

  function handleDismiss() {
    abortRef.current = true;
    sessionStorage.setItem(DISMISS_KEY, latestVersion);
    setShow(false);
  }

  if (!show) return null;

  // Hintergrund-Zustand: schmaler Streifen, keine Buttons, bewegte gestrichelte Linie.
  if (updating) {
    return (
      <div ref={bannerRef} className="install-banner install-banner--progress" role="status" aria-live="polite">
        <div className="ts-update-dashes" aria-hidden="true" />
        <span className="ts-update-label">
          🔄 Aktualisierung läuft – die neue Version wird im Hintergrund geladen…
        </span>
      </div>
    );
  }

  // Zustimmungs-Zustand mit Buttons.
  return (
    <div ref={bannerRef} className="install-banner install-banner--update" role="alert">
      <div className="install-banner__content">
        <span className="install-banner__icon">✨</span>
        <div className="install-banner__text">
          <strong>Update verfügbar – v{latestVersion}</strong>
          <span>Du nutzt noch v{APP_VERSION}. Jetzt aktualisieren für neue Features und Fixes.</span>
        </div>
      </div>
      <div className="install-banner__actions">
        <button
          className="install-banner__btn install-banner__btn--ghost"
          onClick={handleDismiss}
        >
          Später
        </button>
        <button
          className="install-banner__btn install-banner__btn--primary"
          onClick={handleConsent}
        >
          Jetzt aktualisieren
        </button>
      </div>
    </div>
  );
}
